import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../hooks/useTheme';
import { ArcMark } from '../ui/ArcMark';
import {
  LogOut, Key, BarChart3, Home, Book, Database, Shield, Menu, X, DollarSign,
  HelpCircle, PanelLeftClose, PanelLeftOpen, Sun, Moon, ScrollText,
} from 'lucide-react';
import LicenceBanner from './LicenceBanner';
import { canManageLicences } from '../../utils/licensing';

/**
 * ARC Portal Kit's shell: a left rail (navigation) + slim topbar
 * (orientation only, no route links) + the page content. Rewritten from the
 * old top-nav bar - same routes, same auth/sign-out behavior, same mobile
 * responsiveness, just restructured to the kit's `.pk-app`/`.pk-rail`/
 * `.pk-topbar`/`.pk-main` grid. Takes `children` and renders them inside
 * `.pk-page`, so App.tsx wraps routes as `<Navbar>{routes}</Navbar>` instead
 * of `<Navbar /><main>{routes}</main>`.
 */
export default function Navbar({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileRailOpen, setMobileRailOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const subscriptionsEnabled = process.env.REACT_APP_ENABLE_SUBSCRIPTIONS !== 'false';

  const navigation = [
    { name: 'Dashboard',      href: '/dashboard',      icon: Home },
    { name: 'API Keys',       href: '/api-keys',       icon: Key },
    { name: 'Usage',          href: '/usage',          icon: BarChart3 },
    ...(subscriptionsEnabled ? [{ name: 'Plans', href: '/plans', icon: DollarSign }] : []),
    { name: 'Models',         href: '/models',         icon: Database },
    { name: 'API Reference',  href: '/api-reference',  icon: Book },
    { name: 'Category Rules', href: '/category-rules', icon: Shield },
    ...(canManageLicences(user) ? [{ name: 'Licences', href: '/licences', icon: ScrollText }] : []),
  ];

  const activeItem = navigation.find((item) => location.pathname === item.href);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close the mobile rail overlay on route change, same as the old mobile menu did.
  useEffect(() => {
    setMobileRailOpen(false);
  }, [location.pathname]);

  return (
    <div className={`pk-app ${collapsed ? 'is-collapsed' : ''}`}>
      <aside className={`pk-rail ${mobileRailOpen ? 'is-open' : ''}`}>
        <div className="pk-rail-brand">
          <ArcMark variant="current" size={20} style={{ color: 'var(--arc-accent)' }} />
          <span className="txt">ARC</span>
          <span className="sub">Portal</span>
        </div>

        <nav className="pk-rail-group" aria-label="Main navigation">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                aria-current={isActive ? 'page' : undefined}
                className={`pk-nav-item ${isActive ? 'is-active' : ''}`}
                title={item.name}
              >
                <Icon className="pk-i" size={17} />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="pk-rail-foot" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setUserMenuOpen((open) => !open)}
            className="pk-rail-user"
            aria-expanded={userMenuOpen}
            title={user?.email}
          >
            <span className="pk-avatar">{(user?.email || '?').slice(0, 1).toUpperCase()}</span>
            <span className="meta" style={{ minWidth: 0, overflow: 'hidden' }}>
              <span className="pk-small" style={{ display: 'block', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                {user?.email}
              </span>
            </span>
          </button>

          {userMenuOpen && (
            <div
              role="menu"
              className="pk-card"
              style={{ position: 'absolute', bottom: '100%', left: 12, right: 12, marginBottom: 6, padding: 6, zIndex: 50 }}
            >
              <Link
                to="/guide"
                onClick={() => setUserMenuOpen(false)}
                className="pk-nav-item"
                style={{ minHeight: 34 }}
              >
                <HelpCircle className="pk-i" size={16} />
                <span>How it all works</span>
              </Link>
              <button
                type="button"
                onClick={() => { setUserMenuOpen(false); logout(); }}
                className="pk-nav-item"
                style={{ minHeight: 34, width: '100%', border: 0, background: 'none', cursor: 'pointer', color: 'var(--arc-danger)' }}
              >
                <LogOut className="pk-i" size={16} style={{ color: 'inherit' }} />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>
      </aside>

      {mobileRailOpen && (
        <div
          className="pk-scrim"
          onClick={() => setMobileRailOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 50 }}
        />
      )}

      <div className="pk-main">
        <div className="pk-topbar">
          <button
            type="button"
            onClick={() => setMobileRailOpen((open) => !open)}
            className="pk-btn pk-btn-quiet pk-btn-icon"
            aria-label="Toggle navigation"
            style={{ display: 'none' }}
            data-mobile-toggle
          >
            {mobileRailOpen ? <X size={18} /> : <Menu size={18} />}
          </button>

          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="pk-btn pk-btn-quiet pk-btn-icon"
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            data-desktop-toggle
          >
            {collapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
          </button>

          <div className="pk-crumbs">
            <strong>{activeItem?.name ?? 'Portal'}</strong>
          </div>

          <div className="pk-topbar-actions">
            <button
              type="button"
              onClick={toggleTheme}
              className="pk-btn pk-btn-quiet pk-btn-icon"
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
            </button>
            <Link
              to="/guide"
              title="How it all works"
              className={`pk-btn pk-btn-quiet pk-btn-icon ${location.pathname === '/guide' ? 'is-active' : ''}`}
              aria-label="Guide"
            >
              <HelpCircle size={17} />
            </Link>
          </div>
        </div>

        <div className="pk-page">
          <LicenceBanner />
          {children}
        </div>
      </div>
    </div>
  );
}
